<template>
  <div class="checkbox-wrapper">
    <div :class="['checkbox', { disabled: props.isDisabled }]">
      <component
        :is="isChecked ? CheckmarkFilledIcon : CheckmarkSquareIcon"
        :id="id"
        :aria-checked="isChecked"
        :aria-labelledby="`${id}-label`"
        role="checkbox"
        tabindex="0"
        @click="handleChange"
        @keydown.space="handleChange"
      />
    </div>
    <div v-if="safeTitle || safeCaption" class="checkbox-text-container">
      <div
        v-if="safeTitle"
        :id="`${id}-label`"
        class="title"
        v-html="safeTitle"
      />
      <div
        v-if="safeCaption"
        :id="`${id}-label`"
        :class="{ 'without-title': !title }"
        class="label"
        v-html="safeCaption"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import DOMPurify from 'dompurify';

import '@/v2/design-system/styles/main.css';
import CheckmarkFilledIcon from '@/v2/design-system/assets/icons/CheckmarkFilledIcon.vue';
import CheckmarkSquareIcon from '@/v2/design-system/assets/icons/CheckmarkSquareIcon.vue';

const props = defineProps({
  id: { type: String, default: 'bm-checkbox' },
  isChecked: {
    type: Boolean,
    default: false
  },
  title: {
    type: String
  },
  caption: {
    type: String
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  onChange: {
    type: Function,
    required: true
  }
});

const isChecked = ref(props.isChecked);

watch(
  () => props.isChecked,
  newValue => {
    isChecked.value = newValue;
  }
);

const safeTitle = computed(() => {
  return DOMPurify.sanitize(props.title);
});

const safeCaption = computed(() => {
  return DOMPurify.sanitize(props.caption);
});

const handleChange = event => {
  isChecked.value = !isChecked.value;
  props.onChange(isChecked);
};
</script>

<style scoped>
.checkbox-wrapper {
  display: inline-flex;
  align-items: flex-start;
  font-family: var(--font-family-body), sans-serif;
  padding: var(--pad-space-sp-0) var(--pad-space-sp-4);
  gap: 12px;
}

.checkbox {
  height: var(--header-h-3-semi-size);
  width: var(--header-h-3-semi-size);
}

.checkbox svg {
  user-select: none;
  height: var(--header-h-3-semi-size);
  width: var(--header-h-3-semi-size);
  cursor: pointer;
}

.checkbox.disabled {
  & svg {
    cursor: not-allowed;
  }
}

.checkbox-text-container {
  cursor: default;

  & > p {
    margin: 0;
  }
}

.title {
  color: var(--neutral-text-icon-title);
  font-size: var(--body-small-med-size);
  font-weight: var(--body-small-med-weight);
  line-height: var(--line-height-lead-sm);
}

.caption {
  color: var(--neutral-text-icon-label);
  font-size: var(--body-small-reg-size);
  font-weight: var(--body-small-reg-weight);
  line-height: var(--line-height-lead-sm);
}

.caption.without-title {
  color: var(--neutral-text-icon-body);
  font-size: var(--body-small-reg-size);
  font-weight: var(--body-small-reg-weight);
  line-height: var(--line-height-lead-sm);
}
</style>
